import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Layers, Check, X, Upload, Sliders, Download, Zap, Shirt, Scissors,
  MonitorDown, ArrowRight, Loader2, ShieldCheck, Sparkles, Star, RotateCcw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const HERO_TEE =
  "M115,55 L70,80 L20,150 L75,210 L120,178 L120,470 L340,470 L340,178 L385,210 L440,150 L390,80 L345,55 C330,92 288,112 230,112 C172,112 130,92 115,55 Z";
const SKATER_IMG = "/skater.png";

const HERO_FABRICS = {
  black: { name: "Black", fill: "#191919", shade: "#0d0d0d", collar: "#000000" },
  white: { name: "White", fill: "#f2f2f2", shade: "#d2d2d2", collar: "#c4c4c4" },
  navy: { name: "Navy", fill: "#20293f", shade: "#141b2c", collar: "#0f1421" },
};

function HeroTee({ feather, label, accent, fabric = "black" }) {
  const f = HERO_FABRICS[fabric] || HERO_FABRICS.black;
  return (
    <div className="border border-[#262626] bg-[#0f0f0f]">
      <div className="px-3 py-2 border-b border-[#262626] flex items-center gap-2">
        {accent ? (
          <Check className="w-4 h-4 text-[#D4FF00]" strokeWidth={3} />
        ) : (
          <X className="w-4 h-4 text-[#777]" strokeWidth={3} />
        )}
        <span className={`font-mono text-[10px] uppercase tracking-widest ${accent ? "text-[#D4FF00]" : "text-[#888]"}`}>
          {label}
        </span>
      </div>
      <div className="relative aspect-square bg-[#0b0b0b]">
        <svg viewBox="0 0 460 520" className="w-full h-full">
          <path d={HERO_TEE} fill={f.fill} stroke={f.shade} strokeWidth="2" />
          <path
            d="M175,58 C195,86 265,86 285,58 C270,72 255,80 230,80 C205,80 190,72 175,58 Z"
            fill={f.collar}
            opacity="0.9"
          />
        </svg>
        <div className="absolute flex items-center justify-center" style={{ left: "30%", top: "25%", width: "40%", height: "44%" }}>
          <img
            src={SKATER_IMG}
            alt="graphic on shirt"
            className="max-w-full max-h-full object-contain"
            style={
              feather
                ? {
                    maskImage: "radial-gradient(ellipse closest-side at center, rgba(0,0,0,1) 12%, rgba(0,0,0,0.55) 48%, rgba(0,0,0,0) 92%)",
                    WebkitMaskImage: "radial-gradient(ellipse closest-side at center, rgba(0,0,0,1) 12%, rgba(0,0,0,0.55) 48%, rgba(0,0,0,0) 92%)",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskSize: "100% 100%",
                    maskSize: "100% 100%",
                  }
                : { outline: "1px solid rgba(255,255,255,0.18)" }
            }
          />
        </div>
      </div>
    </div>
  );
}


function useCheckout() {
  const [loading, setLoading] = useState(false);
  const buy = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "printfade_lifetime",
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setLoading(false);
      alert("Could not start checkout. Please try again.");
    }
  };
  return { buy, loading };
}

const Label = ({ children }) => (
  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4FF00]">{children}</span>
);

export default function Landing() {
  const { buy, loading } = useCheckout();
  const [heroFabric, setHeroFabric] = useState("black");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-overlay overflow-x-hidden">
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-50 border-b border-[#1c1c1c] bg-[#0A0A0A]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-[#D4FF00] flex items-center justify-center">
              <Layers className="w-4 h-4 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-head font-extrabold text-lg tracking-tighter">
              PrintFade <span className="text-[#D4FF00]">Studio</span>
              <sup className="text-[10px] text-[#666] ml-0.5">™</sup>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/studio"
              data-testid="nav-launch-studio"
              className="hidden sm:inline font-mono text-[11px] uppercase tracking-widest text-[#A3A3A3] hover:text-white transition-colors"
            >
              Launch Studio
            </Link>
            <button
              onClick={buy}
              disabled={loading}
              data-testid="nav-buy-btn"
              className="bg-[#D4FF00] text-black font-bold text-sm px-4 py-2 hover:bg-white transition-colors flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Get Access – $9.95
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="pt-28 pb-20 px-5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <div className="mb-5"><Label>DTF · DTG · Print-on-Demand</Label></div>
            <h1 className="font-head font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
              Say Goodbye to Harsh Box Prints. <span className="text-[#D4FF00]">Perfect Photographic Fades</span> on Any Color Apparel.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#A3A3A3] max-w-xl leading-relaxed">
              The ultra-simple, 300 DPI edge-blending desktop tool built specifically for DTF, DTG, and
              Print-on-Demand creators. No bloated software, no subscriptions.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                onClick={buy}
                disabled={loading}
                data-testid="hero-buy-btn"
                className="group bg-[#D4FF00] text-black font-bold text-base px-7 py-4 hover:bg-white transition-colors flex items-center gap-3"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" strokeWidth={2.5} />}
                Get Instant Desktop Access – $9.95
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <div className="mt-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#666]">
              <ShieldCheck className="w-4 h-4 text-[#D4FF00]" /> One-time payment · Lifetime access
            </div>
          </motion.div>

          {/* Hero comparison */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="relative">
            <div className="flex items-center justify-between mb-3" data-testid="hero-fabric-toggle">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#666]">Preview fabric</span>
              <div className="flex items-center gap-2">
                {Object.entries(HERO_FABRICS).map(([key, f]) => (
                  <button
                    key={key}
                    onClick={() => setHeroFabric(key)}
                    data-testid={`hero-fabric-${key}`}
                    title={f.name}
                    aria-label={f.name}
                    className={`w-6 h-6 border-2 transition-transform hover:scale-110 ${
                      heroFabric === key ? "border-[#D4FF00] scale-110" : "border-[#333]"
                    }`}
                    style={{ backgroundColor: f.fill }}
                  />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-testid="hero-comparison">
              <HeroTee feather={false} accent={false} fabric={heroFabric} label="Before: Harsh Rectangle Edges" />
              <HeroTee feather={true} accent={true} fabric={heroFabric} label="After: Seamless 300 DPI Fade" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM VS FIX */}
      <section className="py-20 px-5 border-t border-[#1c1c1c]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <Label>The Difference</Label>
            <h2 className="font-head font-extrabold tracking-tighter text-3xl sm:text-4xl mt-3">
              Bloated Suites vs. The PrintFade Fix
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="border border-[#262626] bg-[#0f0f0f] p-8"
            >
              <div className="flex items-center gap-2 mb-6">
                <X className="w-5 h-5 text-[#FF3B30]" strokeWidth={3} />
                <h3 className="font-head font-bold text-xl tracking-tight text-[#A3A3A3]">Standard Graphic Suites</h3>
              </div>
              <ul className="space-y-4">
                {[
                  "Bloated $30/month subscriptions",
                  "10-step clipping mask workflows",
                  "Complicated, confusing layer stacks",
                  "Slow export times & heavy installs",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-[#8a8a8a]">
                    <X className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="line-through decoration-[#FF3B30]/50">{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
              className="border-2 border-[#D4FF00] bg-[#0f0f0f] p-8 shadow-[0_0_50px_-20px_rgba(212,255,0,0.5)]"
            >
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-[#D4FF00]" strokeWidth={2.5} />
                <h3 className="font-head font-bold text-xl tracking-tight">PrintFade Studio</h3>
              </div>
              <ul className="space-y-4">
                {[
                  "One-time $9.95 lifetime price",
                  "Single-slider 1% to 100% feathering",
                  "Live front / back shirt color preview",
                  "Instant 300 DPI transparent PNG exports",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-white font-medium">
                    <Check className="w-5 h-5 text-[#D4FF00] shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-5 border-t border-[#1c1c1c]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <Label>How It Works</Label>
            <h2 className="font-head font-extrabold tracking-tighter text-3xl sm:text-4xl mt-3">
              Print-Ready in 3 Steps
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Upload, n: "01", t: "Drop Your Graphic", d: "Drag any PNG or JPG directly into the canvas. No importing, no setup." },
              { icon: Sliders, n: "02", t: "Dial the Feather Width", d: "Watch the gradient bleed live across black, white, navy, or red shirts." },
              { icon: Download, n: "03", t: "1-Click 300 DPI Export", d: "Download print-ready alpha transparency files for your heat press or POD fulfillment." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="relative border border-[#262626] bg-[#0f0f0f] p-8 hover:border-[#D4FF00]/50 hover:bg-white/[0.02] transition-colors"
              >
                <span className="absolute top-6 right-6 font-head font-extrabold text-5xl text-[#1c1c1c]">{s.n}</span>
                <div className="w-12 h-12 bg-[#D4FF00] flex items-center justify-center mb-6">
                  <s.icon className="w-6 h-6 text-black" strokeWidth={2.5} />
                </div>
                <h3 className="font-head font-bold text-xl tracking-tight mb-2">{s.t}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 px-5 border-t border-[#1c1c1c]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <Label>Live Features</Label>
            <h2 className="font-head font-extrabold tracking-tighter text-3xl sm:text-4xl mt-3">
              Everything You Need. Nothing You Don't.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Zap, t: "1% to 100% Cosine Edge Falloff", d: "Deep, ultra-smooth gradient transitions that dissolve completely into dark fabric." },
              { icon: Shirt, t: "Front Chest & Back Collar Visualizer", d: "Preview your placement on a live garment mockup across five fabric colors." },
              { icon: Scissors, t: "Dual Blending Modes", d: "Perimeter Border Fade for full graphics, plus Subject Edge Softening for cutouts." },
              { icon: MonitorDown, t: "Lightweight PWA Desktop Install", d: "Launches from your Windows taskbar or desktop with one click — zero bloat." },
            ].map((f, i) => (
              <motion.div
                key={f.t}
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="flex items-start gap-5 border border-[#262626] bg-[#0f0f0f] p-7 hover:border-[#D4FF00]/50 transition-colors"
              >
                <div className="w-11 h-11 shrink-0 border border-[#D4FF00]/40 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-[#D4FF00]" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="font-head font-bold text-lg tracking-tight mb-1.5">{f.t}</h3>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed">{f.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 px-5 border-t border-[#1c1c1c]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Label>Pricing</Label>
            <h2 className="font-head font-extrabold tracking-tighter text-3xl sm:text-4xl mt-3">
              One Price. Yours Forever.
            </h2>
          </div>

          {/* Creator review strip */}
          <div className="grid sm:grid-cols-3 gap-4 mb-6" data-testid="review-strip">
            {[
              { q: "Killed my ugly box prints overnight. My black-shirt sales doubled.", a: "Marisa T.", r: "POD Seller" },
              { q: "Cheaper than one month of Photoshop and does the one thing I need.", a: "Devon K.", r: "DTF Printer" },
              { q: "The live shirt preview is genius. Export, press, done in seconds.", a: "Andre L.", r: "Etsy Creator" },
            ].map((rv) => (
              <div key={rv.a} className="border border-[#262626] bg-[#0f0f0f] p-5">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 text-[#D4FF00] fill-[#D4FF00]" />
                  ))}
                </div>
                <p className="text-sm text-[#d4d4d4] leading-relaxed">"{rv.q}"</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#777]">
                  {rv.a} · {rv.r}
                </p>
              </div>
            ))}
          </div>

          {/* 30-Day Money-Back Guarantee badge */}
          <div
            className="flex items-center justify-center gap-3 border border-[#D4FF00]/40 bg-[#D4FF00]/[0.05] px-5 py-4 mb-8"
            data-testid="guarantee-badge"
          >
            <RotateCcw className="w-6 h-6 text-[#D4FF00] shrink-0" strokeWidth={2} />
            <div className="text-center sm:text-left">
              <p className="font-head font-bold text-base tracking-tight">30-Day Money-Back Guarantee</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                Not blown away? Full refund, no questions asked.
              </p>
            </div>
          </div>

          <div className="max-w-lg mx-auto">
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="border-2 border-[#D4FF00] bg-[#0f0f0f] p-10 text-center shadow-[0_0_60px_-20px_rgba(212,255,0,0.5)]"
            data-testid="pricing-card"
          >
            <div className="flex items-baseline justify-center gap-1">
              <span className="font-head font-extrabold text-6xl tracking-tighter">$9.95</span>
              <span className="font-mono text-sm text-[#A3A3A3] uppercase tracking-widest ml-2">one-time</span>
            </div>
            <div className="mt-6 space-y-3 text-sm">
              {["Lifetime Access", "Free Future Updates", "300 DPI Transparent Exports", "PWA Desktop Install"].map((t) => (
                <div key={t} className="flex items-center justify-center gap-2 text-white">
                  <Check className="w-4 h-4 text-[#D4FF00]" strokeWidth={3} /> {t}
                </div>
              ))}
            </div>
            <button
              onClick={buy}
              disabled={loading}
              data-testid="pricing-buy-btn"
              className="mt-8 w-full bg-[#D4FF00] text-black font-bold text-base px-6 py-4 hover:bg-white transition-colors flex items-center justify-center gap-3"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <MonitorDown className="w-5 h-5" strokeWidth={2.5} />}
              Buy Now &amp; Install to Desktop
            </button>
            <div className="mt-5 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#666]">
              <ShieldCheck className="w-3.5 h-3.5 text-[#D4FF00]" />
              Instant Setup · 100% Secure Checkout via Stripe
            </div>
          </motion.div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#1c1c1c] px-5 py-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-mono text-[11px] text-[#555]" data-testid="footer-creator">by D. Hackman</span>
          <span className="font-mono text-[11px] text-[#777]" data-testid="footer-copyright">
            Copyright © 2026 PrintFade Studio. All rights reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}
